import {
  dataPointPool,
  normalizeChartDataPoint,
  normalizeChartDataPoints,
  type ChartDataInputPoint,
  type ChartDataPoint,
} from "./data";

export type ChartDataStore = {
  getData(): ChartDataPoint[];
  setData(points: Array<ChartDataInputPoint | ChartDataPoint>): void;
  append(point: ChartDataInputPoint | ChartDataPoint): void;
  appendMany(points: Array<ChartDataInputPoint | ChartDataPoint>): void;
  clear(): void;
  clearData(): void;
  receive(raw: unknown): void;
  subscribe(callback: () => void): () => void;
};

export type ChartDataStoreOptions = {
  symbol?: string;
  pipSize?: number;
  initialData?: Array<ChartDataInputPoint | ChartDataPoint>;
};

function toObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function parseDataPayload(raw: unknown): Array<ChartDataInputPoint | ChartDataPoint> {
  const text = typeof raw === "string" ? raw : null;
  let parsed: unknown = raw;

  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      return [];
    }
  }

  if (Array.isArray(parsed)) {
    parsed = { data: parsed };
  }

  const message = toObject(parsed);
  if (!message) return [];

  const series =
    (Array.isArray(message.ticks) && message.ticks) ||
    (Array.isArray(message.candles) && message.candles) ||
    (Array.isArray(message.ohlc) && message.ohlc) ||
    (Array.isArray(message.prices) && message.prices) ||
    (Array.isArray(message.data) && message.data) ||
    (Array.isArray(message.message) && message.message) ||
    null;

  const points = Array.isArray(series) ? series : [message];
  const output: Array<ChartDataInputPoint | ChartDataPoint> = [];

  for (const item of points) {
    const point = toObject(item);
    if (!point) continue;
    output.push({
      epoch: typeof point.epoch === "number" ? point.epoch : typeof point.time === "number" ? point.time : undefined,
      epoch_ms:
        typeof point.epoch_ms === "number"
          ? point.epoch_ms
          : typeof point.epoch === "number"
            ? point.epoch * 1000
            : typeof point.time === "number"
              ? point.time * 1000
              : undefined,
      quote:
        typeof point.quote === "number"
          ? point.quote
          : typeof point.price === "number"
            ? point.price
            : typeof point.close === "number"
              ? point.close
              : typeof point.bid === "number"
                ? point.bid
                : typeof point.ask === "number"
                  ? point.ask
                  : typeof point.value === "number"
                    ? point.value
                    : undefined,
      symbol: typeof point.symbol === "string" ? point.symbol : undefined,
      precision: typeof point.precision === "number" ? point.precision : undefined,
      pip_size: typeof point.pip_size === "number" ? point.pip_size : undefined,
      pipSize: typeof point.pipSize === "number" ? point.pipSize : undefined,
    });
  }

  return output;
}

export function createChartDataStore(options: ChartDataStoreOptions = {}): ChartDataStore {
  let data: ChartDataPoint[] = normalizeChartDataPoints(
    options.initialData ?? [],
    options.symbol ?? "",
    options.pipSize ?? 3,
  );
  const listeners = new Set<() => void>();

  const notify = () => {
    for (const listener of listeners) listener();
  };

  const releaseData = () => {
    for (const point of data) dataPointPool.release(point);
  };

  return {
    getData() {
      return data;
    },
    setData(points) {
      releaseData();
      data = normalizeChartDataPoints(points, options.symbol ?? "", options.pipSize ?? 3);
      notify();
    },
    append(point) {
      const next = normalizeChartDataPoint(point, options.symbol ?? "", options.pipSize ?? 3);
      data = [...data, next].sort((a, b) => a.epoch - b.epoch);
      notify();
    },
    appendMany(points) {
      const normalized = normalizeChartDataPoints(points, options.symbol ?? "", options.pipSize ?? 3);
      data = [...data, ...normalized].sort((a, b) => a.epoch - b.epoch);
      notify();
    },
    clear() {
      releaseData();
      data = [];
      notify();
    },
    clearData() {
      releaseData();
      data = [];
      notify();
    },
    receive(raw) {
      const points = parseDataPayload(raw);
      if (!points.length) return;
      data = [...data, ...normalizeChartDataPoints(points, options.symbol ?? "", options.pipSize ?? 3)].sort((a, b) => a.epoch - b.epoch);
      notify();
    },
    subscribe(callback) {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
  };
}

export function createChartDataAdapter(options: ChartDataStoreOptions = {}): ChartDataStore {
  return createChartDataStore(options);
}
